import React, { useState, useEffect } from "react";
import { HashRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import CardapioPublico from "./pages/CardapioPublico";
import LoginModal from "./components/modals/LoginModal";
import ReviewPublic from "./pages/ReviewPublic";
import SaidaPage from "./features/mesa-saida/SaidaPage";
import { Toaster } from "./components/ui/sonner";
import { initApiConfig, getSettings, getApiBase } from "./config/api";
import { getToken, removeToken } from "./utils/tokenStorage";
import SuporteEntrar from "./pages/SuporteEntrar";
import { getTenantSlug } from "./utils/tenantScope";
import axios from "axios";
import { initAudioKeepAlive } from "./utils/audioKeepAlive";
import { aplicarTema, guardarPadraoDaLoja, temaQueVale } from "./features/tema";
import "./App.css";

// Guard inicial: se o ultimo slug logado difere do slug atual, limpar sessao (logout automatico)
(function detectTenantSwitch() {
  try {
    const currentSlug = getTenantSlug();
    const lastSlug = localStorage.getItem("__last_slug__");
    if (lastSlug && lastSlug !== currentSlug) {
      // Limpar chaves herdadas do tenant anterior
      ["user", "client", "cliente_token", "fornecedores", "mesas", "tenant_trial", "token"].forEach(k => {
        try { localStorage.removeItem(k); } catch {}
      });
      // Limpar cookie do token legado (o tokenStorage novo usa chave por slug)
      document.cookie = "token=;path=/;max-age=0";
    }
    localStorage.setItem("__last_slug__", currentSlug);
  } catch {}
})();

// Interceptor global: limpar token e recarregar ao receber 401
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      const token = getToken();
      if (token) {
        removeToken();
        window.location.hash = '#/cardapio';
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// ErrorBoundary global — evita tela branca em caso de erro JS
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Erro capturado:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8f9fa", fontFamily: "sans-serif", padding: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#333" }}>Algo deu errado</h1>
          <p style={{ color: "#666", marginBottom: 24, textAlign: "center", maxWidth: 400 }}>
            Ocorreu um erro inesperado. Clique no botão abaixo para recarregar o sistema.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "12px 32px", fontSize: 16, fontWeight: 600, color: "#fff", background: "#f97316", border: "none", borderRadius: 8, cursor: "pointer" }}
          >
            Recarregar Sistema
          </button>
          <p style={{ marginTop: 16, fontSize: 12, color: "#999" }}>
            {this.state.error?.message || "Erro desconhecido"}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Componente wrapper para o Cardápio com navegação
function CardapioWrapper({ setIsAuthenticated }) {
  const navigate = useNavigate();

  const handleAdminLogin = (userData) => {
    setIsAuthenticated(true);
    // Redirecionar para o dashboard admin
    navigate("/admin/");
  };

  // Mesa QR: ler o QR de OUTRA mesa na mesma aba só troca o código no
  // endereço (a página não recarrega). Sem a chave, o cardápio seguia montado
  // com a sessão da mesa anterior e travava em "Não dá para pedir agora".
  const { mesaToken } = useParams();

  return (
    <CardapioPublico key={mesaToken || "cardapio"} onAdminLogin={handleAdminLogin} />
  );
}

// Extrair slug do subdomínio para detecção de trial/subdomínios reservados
// (usa regra diferente do tenantScope.getTenantSlug: retorna 'app'/'admin' em vez de 'default')
function _getLocationSlug() {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") return null;
  if (hostname === "usenucleo.com.br") return null;
  const match = hostname.match(/^([^.]+)\.usenucleo\.com\.br$/);
  if (!match) return null;
  const slug = match[1];
  if (["www", "app", "api", "admin"].includes(slug)) return slug;
  return slug;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Verificar token no estado inicial
    return !!getToken();
  });
  const [skipLogin, setSkipLogin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenantError, setTenantError] = useState(null); // "not_found" | "suspended" | "redirect"
  const [lojistaLoginOpen, setLojistaLoginOpen] = useState(false); // login do lojista na tela de teste vencido

  useEffect(() => {
    const init = async () => {
      // Verificar subdomínio para multi-tenant
      const slug = _getLocationSlug();

      // app.usenucleo.com.br → redirecionar para landing page
      if (slug === "app" || slug === "www") {
        window.location.href = "https://usenucleo.com.br";
        return;
      }

      // Se tem slug de tenant, validar antes de carregar
      if (slug && !["api", "admin"].includes(slug)) {
        try {
          const res = await fetch(`/api/public/tenant-info`);
          const data = await res.json();
          if (!data.found) {
            setTenantError("not_found");
            setLoading(false);
            return;
          }
          if (data.indisponivel) {
            // Teste grátis vencido: cardápio fora do ar até pagar a 1ª mensalidade.
            setTenantError("teste_vencido");
            setLoading(false);
            return;
          }
          if (data.status === "suspended" || data.status === "cancelled") {
            setTenantError("suspended");
            setLoading(false);
            return;
          }
          // Tenant válido — atualizar título com nome do restaurante
          if (data.name) {
            document.title = data.name;
          }
          // Salvar info de trial para o Dashboard usar
          if (data.status === 'trial' && data.trial_expires_at) {
            localStorage.setItem('tenant_trial', JSON.stringify({
              status: data.status,
              plan: data.plan,
              trial_expires_at: data.trial_expires_at
            }));
          } else {
            localStorage.removeItem('tenant_trial');
          }
        } catch (err) {
          console.error("[Tenant] Erro ao validar tenant:", err);
          // Continuar normalmente em caso de erro de rede
        }
      }

      // Inicializar configuração da API
      await initApiConfig();

      // Manter áudio do navegador ativo (evita autoplay policy suspender notificações)
      initAudioKeepAlive();

      // Atualizar favicon com logo da empresa
      updateFavicon();

      // Carregar aparência do backend (tema + cor primária)
      try {
        const apiBase = getApiBase();
        // Só tema e cor, sem login (achado 9, 27/10/2026: /settings devolvia
        // todos os ajustes da loja, com tokens, para qualquer um)
        const res = await fetch(`${apiBase}/public/aparencia`);
        const sData = await res.json();
        // Tema: aplicar antes de renderizar. settings.theme é o padrão da
        // LOJA; quem escolheu um tema só para si (menu da foto) fica com o dele.
        guardarPadraoDaLoja(sData.theme);
        const tema = temaQueVale(sData.theme);
        if (tema) aplicarTema(tema);
        // Cor primária
        if (sData.primary_color) {
          const root = document.documentElement;
          root.style.setProperty("--primary", sData.primary_color);
          root.style.setProperty("--ring", sData.primary_color);
          root.style.setProperty("--chart-1", sData.primary_color);
        }
      } catch (_) {
        // Fallback: usar localStorage para tema
        if (localStorage.getItem("darkMode") === "true") {
          document.documentElement.classList.add("dark");
        }
      }

      // Verificar modo sem login
      const settings = getSettings();
      if (settings.skipLogin) {
        setSkipLogin(true);
        setIsAuthenticated(true);
      } else {
        // Verificar se há token de admin
        const token = getToken();
        setIsAuthenticated(!!token);
      }

      setLoading(false);
    };

    init();
  }, []);

  // Função para atualizar o favicon com a logo da empresa
  const updateFavicon = async () => {
    try {
      const apiBase = getApiBase();
      const faviconUrl = `${apiBase}/favicon?t=${Date.now()}`;
      
      // Tentar carregar o favicon da empresa usando GET
      const response = await fetch(faviconUrl);
      
      if (response.ok) {
        // Se existir, atualizar o favicon
        // Remover favicons antigos
        const oldIcons = document.querySelectorAll("link[rel*='icon']");
        oldIcons.forEach(icon => icon.remove());
        
        // Criar novo favicon
        const link = document.createElement('link');
        link.type = 'image/png';
        link.rel = 'icon';
        link.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(link);
        
        // Também atualizar apple-touch-icon
        const oldAppleIcons = document.querySelectorAll("link[rel*='apple-touch-icon']");
        oldAppleIcons.forEach(icon => icon.remove());
        
        const appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(appleIcon);
      } else {
        // Se não houver logo, usar logo padrão
        updateFaviconWithDefault();
      }
    } catch (error) {
      // Se houver erro, usar logo padrão
      console.log('[Favicon] Erro ao carregar favicon da empresa, usando padrão:', error);
      updateFaviconWithDefault();
    }
  };

  // Função para usar favicon padrão
  const updateFaviconWithDefault = () => {
    // Remover favicons antigos
    const oldIcons = document.querySelectorAll("link[rel*='icon']");
    oldIcons.forEach(icon => icon.remove());
    
    // Usar logo padrão do Núcleo
    const link = document.createElement('link');
    link.type = 'image/png';
    link.rel = 'icon';
    link.href = '/logo-nucleo.png';
    document.getElementsByTagName('head')[0].appendChild(link);
    
    const appleIcon = document.createElement('link');
    appleIcon.rel = 'apple-touch-icon';
    appleIcon.href = '/logo-nucleo.png';
    document.getElementsByTagName('head')[0].appendChild(appleIcon);
  };

  // Teste grátis vencido: "Loja temporariamente indisponível" + entrada discreta do lojista
  // (o login mostra o aviso de pagamento em destaque).
  if (tenantError === "teste_vencido") {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8f9fa", fontFamily: "sans-serif", padding: 24 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#333" }}>Loja temporariamente indisponível</h1>
        <p style={{ color: "#666", marginBottom: 24, textAlign: "center", maxWidth: 400 }}>
          Esta loja não está recebendo pedidos agora. Volte em breve.
        </p>
        <button
          type="button"
          onClick={() => setLojistaLoginOpen(true)}
          style={{ background: "none", border: "none", color: "#999", fontSize: 13, textDecoration: "underline", cursor: "pointer" }}
        >
          Sou o lojista
        </button>
        <LoginModal isOpen={lojistaLoginOpen} onClose={() => setLojistaLoginOpen(false)} onLoginSuccess={() => {}} />
      </div>
    );
  }

  // Tela de erro de tenant
  if (tenantError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8f9fa", fontFamily: "sans-serif", padding: 24 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>
          {tenantError === "not_found" ? "🔍" : "⚠️"}
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#333" }}>
          {tenantError === "not_found" ? "Restaurante não encontrado" : "Conta suspensa"}
        </h1>
        <p style={{ color: "#666", marginBottom: 24, textAlign: "center", maxWidth: 400 }}>
          {tenantError === "not_found"
            ? "O endereço que você acessou não corresponde a nenhum restaurante cadastrado no Núcleo."
            : "A conta deste restaurante está temporariamente suspensa. Entre em contato com o suporte."}
        </p>
        <a
          href="https://usenucleo.com.br"
          style={{ padding: "12px 32px", fontSize: 16, fontWeight: 600, color: "#fff", background: "#f97316", border: "none", borderRadius: 8, cursor: "pointer", textDecoration: "none" }}
        >
          Ir para o site
        </a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando Núcleo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <HashRouter>
        <Routes>
          {/* Página pública de avaliação de pedido (link enviado por WhatsApp) */}
          <Route path="/review/:token" element={<ReviewPublic />} />

          {/* Entrada de suporte pelo CRM: troca o ticket por uma sessão.
              Rota aberta de propósito — quem protege é o ticket (uso único,
              2 minutos, emitido só pelo CRM autenticado). */}
          <Route
            path="/suporte"
            element={<SuporteEntrar setIsAuthenticated={setIsAuthenticated} />}
          />

          {/* Cardápio Público - Tela Inicial */}
          <Route
            path="/cardapio"
            element={<CardapioWrapper setIsAuthenticated={setIsAuthenticated} />}
          />
          {/* Mesa QR: link impresso na mesa, no formato /#/mesa1/<token>. O
              primeiro trecho e' o nome da mesa e serve so' pra o lojista
              conferir a olho qual cartao e' de qual mesa — quem identifica de
              verdade e' o token. As rotas com prefixo fixo ficam pra tras dos
              QRs ja impressos. Rota generica por ultimo: o React Router poe
              trecho estatico na frente de trecho dinamico, entao /cardapio,
              /admin/* e companhia continuam ganhando dela. */}
          <Route
            path="/mesa/:mesaToken"
            element={<CardapioWrapper setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route
            path="/cardapio/mesa/:mesaToken"
            element={<CardapioWrapper setIsAuthenticated={setIsAuthenticated} />}
          />
          {/* Comprovante do cliente / conferir saída da equipe (Mesa QR,
              24/09/2026): o QR de saída carrega este link. Logado → tela da
              equipe; cliente → o comprovante dele. */}
          <Route
            path="/saida/qr/:codigo"
            element={<SaidaPage isAuthenticated={isAuthenticated || skipLogin} />}
          />
          <Route
            path="/:mesaSlug/:mesaToken"
            element={<CardapioWrapper setIsAuthenticated={setIsAuthenticated} />}
          />
          {/* Deep link de produto pra divulgacao (Insta/WhatsApp/anuncio):
              cliente abre URL no formato /cardapio/p/<slug> e o popup do produto
              correspondente abre direto. Slug derivado do nome do produto. */}
          <Route
            path="/cardapio/p/:productSlug"
            element={<CardapioWrapper setIsAuthenticated={setIsAuthenticated} />}
          />
          
          {/* Rotas diretas do menu superior - Requer autenticação */}
          {/* Essas rotas renderizam o Dashboard diretamente, mantendo a URL limpa */}
          <Route
            path="/delivery/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          <Route
            path="/mesas/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          <Route
            path="/comandas/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          <Route
            path="/cardapio-admin/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          <Route
            path="/balcao/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          {/* Totem (25/10/2026): aba própria no cabeçalho, autoatendimento em tela cheia */}
          <Route
            path="/totem/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          <Route
            path="/chatbot/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          
          {/* Dashboard Admin - Requer autenticação */}
          <Route
            path="/admin/*"
            element={
              isAuthenticated || skipLogin ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          
          {/* Rota raiz - redireciona para admin se logado, senão cardápio */}
          <Route
            path="/"
            element={
              isAuthenticated || skipLogin ? (
                <Navigate to="/admin/" replace />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
          
          {/* Qualquer outra rota */}
          <Route
            path="/*"
            element={
              isAuthenticated || skipLogin ? (
                <Navigate to="/admin/" replace />
              ) : (
                <Navigate to="/cardapio" replace />
              )
            }
          />
        </Routes>
      </HashRouter>
      <Toaster position="bottom-left" richColors />
    </div>
  );
}

function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithBoundary;
